import { AxiosError } from 'axios'
import _ from 'lodash'
import React, { useEffect } from 'react'
import apiClient from '../../../services/api-client'
import { useOnlineStatus } from '../../../util/useOnlineStatus'
import { fixProductVariantSpacing } from './fixProductVariantNumber'

export interface VariantResponse {
  masterProductId: number
  itemId: number
  variants: Variant[]
}

export interface Variant {
  productId: number
  productVariantNumber: string
  name: string
  dimensions: Dimension[]
}

export interface Dimension {
  type: number
  id: string
  value: string
  displayOrder?: number
}

interface KnownNames {
  [index: string]: string
}

export enum DimensionTypes {
  SIZE = 3,
  COLOUR = 1,
  STYLE = 4,
}

export const knownDimensionNames: KnownNames = {
  '3': 'Size',
  '1': 'Colour',
  '4': 'Style',
}

// products/5637488875/variants
const useVariants = (productId: number, masterId: number, isCart?: boolean) => {
  const [variants, setVariants] = React.useState<VariantResponse>()
  const [isLoading, setIsLoading] = React.useState(false)
  const [error, setError] = React.useState<AxiosError>()
  const isOnline = useOnlineStatus()

  const { variant, dimensions, defaultDimension } = React.useMemo(() => {
    if (!variants) {
      return { variant: undefined, dimensions: undefined, defaultDimension: undefined }
    }
    let currentVariant = variants?.variants.find((v) => _.isEqual(v.productId, productId))
    if (!currentVariant && variants?.variants.length > 0) {
      variants?.variants.sort((a, b) => {
        return (a.dimensions[0].displayOrder ?? 0) - (b.dimensions[0].displayOrder ?? 0)
      })
      currentVariant = variants?.variants[0]
    }
    if (!currentVariant) {
      return { variant: undefined, dimensions: undefined, defaultDimension: undefined }
    }

    const allDimensions = variants?.variants.flatMap((e) => e.dimensions.flatMap((v) => v))
    interface FlatDimensions {
      [index: string]: Dimension[]
    }

    let dimensionsByType: FlatDimensions = {}
    allDimensions?.forEach((d) => {
      if (!Object.keys(dimensionsByType).includes(d.type.toString())) {
        dimensionsByType = {
          ...dimensionsByType,
          [d.type]: [],
        }
      }

      if (!dimensionsByType[d.type.toString()].find((f) => f.value === d.value)) {
        dimensionsByType[d.type.toString()].push(d)
      }

      if (d.type === DimensionTypes.SIZE) {
        dimensionsByType[d.type.toString()].sort(
          (a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0),
        )
      }
    })

    const getDefaultDimension = () => {
      const size = currentVariant?.dimensions.find((x) => x.type === DimensionTypes.SIZE)?.value
      const colour = currentVariant?.dimensions.find((x) => x.type === DimensionTypes.COLOUR)?.value
      return {
        size,
        colour,
      }
    }

    return {
      variant: currentVariant,
      dimensions: dimensionsByType,
      defaultDimension: getDefaultDimension(),
    }
  }, [variants, isLoading, error, productId, masterId, isOnline])

  useEffect(() => {
    if (isCart && isOnline) {
      ;(async () => {
        setIsLoading(true)
        try {
          const response = await apiClient.get<VariantResponse, AxiosError>(
            `/products/${masterId}/variants`,
          )

          if (response.status.toString().startsWith('2')) {
            const data = response.data
            data.variants.forEach(
              (v) => (v.productVariantNumber = fixProductVariantSpacing(v.productVariantNumber)),
            )

            setVariants(data)
            setError(undefined)
          }
          setIsLoading(false)
        } catch (error) {
          setError(error as AxiosError)
        }
      })()
    }
  }, [masterId, productId, isCart, isOnline])

  return {
    variants: variants,
    error: error,
    isLoading: isLoading,
    variant,
    dimensions,
    defaultDimension,
  }
}

export default useVariants
